import React from 'react';
import { Dimmer, Icon, Loader } from 'semantic-ui-react';
import TablePaginationMenu from '../../../../components/TablePaginationMenu';
import { EstateUnit } from '../../../../types/EstateUnit';
import { UserState } from '../../../../types/UserState';
import UnitActionButton from './UnitActionButton';
import UserRow from './UserRow';
import {
  EmptyStateContainer,
  EmptyStateIcon,
  EmptyStateText,
  PaginationWrapper,
  UsersCardActions,
  UsersCardContainer,
  UsersCardHeader,
  UsersCardIcon,
  UsersCardSubtitle,
  UsersCardTitle,
  UsersCardTitleGroup,
  UsersList
} from './styles';

type UsersCardProps = {
  selectedUnit: EstateUnit | null;
  users: UserState[];
  isLoading: boolean;
  canManage: boolean;
  isUnassigning: boolean;
  totalCount: number;
  currentPage: number;
  onAssignClick: () => void;
  onUnassignClick: (user: UserState) => void;
  onPageChange: (page: number) => void;
};

const UsersCard: React.FC<UsersCardProps> = ({
  selectedUnit,
  users,
  isLoading,
  canManage,
  isUnassigning,
  totalCount,
  currentPage,
  onAssignClick,
  onUnassignClick,
  onPageChange
}) => {
  const renderContent = () => {
    if (!selectedUnit) {
      return (
        <EmptyStateContainer>
          <EmptyStateIcon>
            <Icon name="hand point left outline" />
          </EmptyStateIcon>
          <EmptyStateText>Selecione ao lado uma unidade para gerenciar.</EmptyStateText>
        </EmptyStateContainer>
      );
    }

    if (users.length === 0 && !isLoading) {
      return (
        <EmptyStateContainer>
          <EmptyStateIcon>
            <Icon name="users" />
          </EmptyStateIcon>
          <EmptyStateText>Nenhum usuário vinculado a esta unidade.</EmptyStateText>
        </EmptyStateContainer>
      );
    }

    return (
      <>
        <UsersList aria-label="Usuários vinculados à unidade">
          {users.map(user => (
            <UserRow
              key={user.id}
              user={user}
              canManage={canManage}
              isUnassigning={isUnassigning}
              onUnassign={onUnassignClick}
            />
          ))}
        </UsersList>

        {totalCount > 10 && (
          <PaginationWrapper>
            <TablePaginationMenu
              currentPage={currentPage}
              dataCount={totalCount}
              setCurrentPage={onPageChange}
            />
          </PaginationWrapper>
        )}
      </>
    );
  };

  return (
    <UsersCardContainer aria-busy={isLoading || isUnassigning}>
      <Dimmer active={isLoading || isUnassigning} inverted>
        <Loader
          aria-label={
            isUnassigning ? 'Desvinculando usuário' : 'Carregando usuários'
          }
        />
      </Dimmer>

      <UsersCardHeader>
        <UsersCardTitleGroup>
          <UsersCardIcon aria-hidden>
            <Icon name="users" />
          </UsersCardIcon>

          <div>
            <UsersCardTitle>Usuários</UsersCardTitle>
            <UsersCardSubtitle>
              Gerencie os usuários vinculados a esta unidade.
            </UsersCardSubtitle>
          </div>
        </UsersCardTitleGroup>

        {canManage && selectedUnit && (
          <UsersCardActions>
            <UnitActionButton variant="secondary" onClick={onAssignClick}>
              + Vincular usuários
            </UnitActionButton>
          </UsersCardActions>
        )}
      </UsersCardHeader>

      {renderContent()}
    </UsersCardContainer>
  );
};

export default UsersCard;
